import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import globalInstagramSearch from 'services/audience/audienceFilterService';
import { addOrModifyRule, fetchRules, performRuleAction } from 'services/accounts/accountService';
import { toast } from 'sonner';
import Modal from 'components/modals/AudienceSaveModal';
import AudienceMyRules from './AudienceMyRules';
import SetMyAudience from './SetMyAudience';
import Spinner from 'components/common/Spinner';
import TutorialCompleteModal from 'components/modals/TutorialCompleteModal';
import { useTutorial, TutorialStep } from 'contexts/TutorialContext';
import { useTranslation } from 'react-i18next';
import { logger } from 'utils/logger';

interface Props {}

interface AudienceItem {
  id: string;
  filter_name?: string;
  pk?: string;
  locationId?: string;
  locationName?: string;
  username?: string;
  full_name?: string;
  name?: string;
  profile_pic_url?: string;
  caption?: string;
  thumbnail?: string;
  subtitle?: string;
  search_result_subtitle?: string;
  like_count?: number;
  comment_count?: number;
  user?: { username?: string; full_name?: string; profile_pic_url?: string };
}

interface FilterCriteria {
  type: string;
  filter?: string;
  id?: string;
}

interface SavedRule {
  rule_name: string;
  rule: FilterCriteria[];
  status: string;
}

const AudienceSettings: React.FC<Props> = () => {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';
  const { currentStep, nextStep, isTutorialActive } = useTutorial();

  // -----------------------------------------
  // 1. State Declarations
  // -----------------------------------------
  const [selectedChip, setSelectedChip] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedAudience, setSelectedAudience] = useState<AudienceItem[]>([]);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const isChiploaded = useRef<boolean>(false);
  const [audienceItems, setAudienceItems] = useState<AudienceItem[]>([]);
  const [filterItem, setFilterItems] = useState<FilterCriteria[]>([]);
  const [savedAudienceRules, setSavedAudienceRules] = useState<SavedRule[]>([]);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [ruleName, setRuleName] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [expandedRules, setExpandedRules] = useState<Set<number>>(new Set());

  // Tutorial refs
  const saveRuleButtonRef = useRef<HTMLButtonElement | null>(null);

  const allowedChips = [
    'User (specific user)',
    'User (followers)',
    'User Posts (likers)',
    'User (followings)',
    '#Hashtags Posts (likers)',
    'Places Posts (likers)',
    'Places',
    '#Hashtags',
    'Posts by Keyword',
    'Posts by Keyword (likers)',
	  'Reels by Keyword',
    'Reels by Keyword (likers)'
  ];

  // Fetch saved rules
  useEffect(() => {
    async function loadRules() {
      try {
        setIsLoading(true);
        const updatedRules = (await fetchRules(id)) as SavedRule[];
        setSavedAudienceRules(updatedRules);
      } catch (error) {
        logger.error('Error loading rules:', error);
        toast.error(t('audienceSettings.errors.loadFailed'));
      } finally {
        setIsLoading(false);
      }
    }
    loadRules();
  }, [id]);

  // ----------------------------------------- 
  // 2. Modal Handlers
  // ----------------------------------------- 
  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const saveRule = async (rule: string) => {
    try {
      await addOrModifyRule(id, rule, filterItem);
      const updatedRules = (await fetchRules(id)) as SavedRule[];
      setSavedAudienceRules(updatedRules);
      setFilterItems([]);
      setRuleName('');
      toast.success(t('audienceSettings.messages.ruleSaved'));

      // Tutorial: Complete tutorial after saving rule
      if (isTutorialActive && currentStep === TutorialStep.RULE_NAME_INPUT) {
        setTimeout(() => {
          nextStep(); // Move to COMPLETE step
        }, 1000);
      }
    } catch (error) {
      logger.error('Error saving rule:', error);
      toast.error(t('audienceSettings.errors.saveFailed'));
    }
  };

  // Tutorial: Watch for 10 items in filterItem to show Save Rule button highlight
  useEffect(() => {
    if (isTutorialActive && currentStep === TutorialStep.ADD_SELECTED_ITEMS && filterItem.length >= 10) {
      setTimeout(() => {
        nextStep(); // Move to SAVE_RULE step
        // Scroll to save rule button
        setTimeout(() => {
          if (saveRuleButtonRef.current) {
            saveRuleButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      }, 500);
    }
  }, [filterItem.length, currentStep, isTutorialActive, nextStep]);

  const notify = (message: string) => {
    toast.error(message);
  };

  // ----------------------------------------- 
  // 3. Filter-Related Logic
  // ----------------------------------------- 
  const addFilterItem = () => {
    if (!selectedChip) {
      notify('Please select a chip before adding a filter.');
      return;
    }

    switch (selectedChip) {
      case 'User (specific user)':
      case 'User (followers)':
      case 'User Posts (likers)':
      case 'User (followings)':
        const userItems = selectedAudience.map((item) => ({
          type: selectedChip,
          filter: item.id,
          id: item.pk
        }));
        setFilterItems((prev) => [...prev, ...userItems]);
        break;

      case '#Hashtags Posts (likers)':
      case '#Hashtags':
        const hashtagItems = selectedAudience.map((item) => ({
          type: selectedChip,
          filter: item.filter_name,
          id: item.locationId
        }));
        setFilterItems((prev) => [...prev, ...hashtagItems]);
        break;

      case 'Places':
      case 'Places Posts (likers)':
        const placeItems = selectedAudience.map((item) => ({
          type: selectedChip,
          filter: item.filter_name,
          id: item.pk
        }));
        setFilterItems((prev) => [...prev, ...placeItems]);
        break;

      default:
        notify(`No action defined for this chip: ${selectedChip}`);
    }

    setSearchTerm('');
    setSelectedAudience([]);
  };

  const removeFilterItem = (index: number) => {
    setFilterItems((prev) => prev.filter((_, i) => i !== index));
  };

  // ----------------------------------------- 
  // 4. Category Chips
  // ----------------------------------------- 
  const chips = [
    'User (followers)',
    'User (specific user)',
    'User Posts (likers)',
    'User (followings)',
    'Posts by Keyword',
    'Posts by Keyword (likers)',
    '#Hashtags Posts (likers)',
    'Reels by Keyword',
    'Reels by Keyword (likers)',
    '#Hashtags',
    'Places',
    'Places Posts (likers)'
  ];

  // -----------------------------------------
  // 5. Manual Search Handler
  // -----------------------------------------
  const handleAddKeyword = () => {
    if (!selectedChip || !allowedChips.includes(selectedChip)) {
      toast.error('Please select a valid filter criteria');
      return;
    }

    if (searchTerm.trim() === '') {
      toast.error('Please enter a keyword');
      return;
    }

    // Add keyword directly as filter
    setFilterItems((prev) => [...prev, { type: selectedChip, filter: searchTerm }]);
    toast.success('Keyword added as filter');
    setSearchTerm('');
  };

  const handleSearch = async () => {
    try {
      if (!selectedChip || !allowedChips.includes(selectedChip)) {
        toast.error('Please select a valid filter criteria');
        return;
      }

      if (searchTerm.trim() === '') {
        toast.error('Please enter a search term');
        return;
      }

      let searchType = '';
      if (selectedChip?.toLowerCase().includes('user')) {
        searchType = 'user';
      } else if (selectedChip?.toLowerCase().includes('hashtag')) {
        searchType = 'hashtag';
      }
       else if (selectedChip?.toLowerCase().includes('place')) {
        searchType = 'place';
      } else {
        searchType = 'place';
      }

      setIsSearching(true);
      const items = (await globalInstagramSearch(searchTerm, searchType, id)) as AudienceItem[];
      setAudienceItems(items);
    } catch (error: unknown) {
      logger.error('Error fetching audience items:', error);
      toast.error('Failed to search. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  // ----------------------------------------- 
  // 6. Initial Chip Selection (Side Effect)
  // ----------------------------------------- 
  useEffect(() => {
    if (!isChiploaded.current && chips.length > 0) {
      setSelectedChip(chips[0]);
      isChiploaded.current = true;
    }
  }, [chips]);

  // ----------------------------------------- 
  // 7. Handlers
  // ----------------------------------------- 
  const handleSelectChip = (chip: string) => {
    setSelectedChip(chip);
    setAudienceItems([]);
    setSearchTerm('');
  };

  const handleAudienceChange = (id: string, isChecked: boolean, filter_name: string, pk: string, additionalData?: { caption?: string; thumbnail?: string }) => {
    if (isChecked) {
      setSelectedAudience((prev) => [...prev, { id, filter_name, pk, ...additionalData }]);
    } else {
      setSelectedAudience((prev) => prev.filter((item) => item.id !== id));
    }
  };

  const toggleDropdown = (index: number) => {
    setOpenDropdown((prev) => (prev === index ? null : index));
  };

  // ----------------------------------------- 
  // 8. Saved Rules Management
  // ----------------------------------------- 
  const removeSavedAudienceRule = async (indexToRemove: number) => {
    try {
      await performRuleAction(id, savedAudienceRules[indexToRemove].rule_name, 'Inactive', false);
      const updatedRules = (await fetchRules(id)) as SavedRule[];
      setSavedAudienceRules(updatedRules);
      toast.success(t('audienceSettings.messages.ruleRemoved'));
    } catch (error) {
      logger.error('Error removing rule:', error);
      toast.error(t('audienceSettings.errors.removeFailed'));
    }
  };

  const changeStatusSavedAudienceRule = async (indexToUpdate: number) => {
    try {
      await performRuleAction(
        id,
        savedAudienceRules[indexToUpdate].rule_name,
        savedAudienceRules[indexToUpdate].status === 'Active' ? 'Inactive' : 'Active',
        true
      );
      const updatedRules = (await fetchRules(id)) as SavedRule[];
      setSavedAudienceRules(updatedRules);
      toast.success(t('audienceSettings.messages.ruleUpdated'));
    } catch (error) {
      logger.error('Error updating rule status:', error);
      toast.error(t('audienceSettings.errors.updateFailed'));
    }
  };

  const modifySavedAudienceRule = (indexToModify: number) => {
    setRuleName(savedAudienceRules[indexToModify].rule_name);
    setFilterItems(savedAudienceRules[indexToModify].rule);
  };

  const toggleExpandRule = (index: number) => {
    setExpandedRules((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // ----------------------------------------- 
  // 9. Close Dropdown on Outside Click
  // ----------------------------------------- 
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // ----------------------------------------- 
  // 10. Render
  // ----------------------------------------- 
  
  // Show spinner while loading initial data
  if (isLoading) {
    return <Spinner size="large" text={t('audienceSettings.messages.loading')} fullPage />;
  }

  return (
    <div className="famous-clicker-audience-account-view-tab-container">
      {/* Header Section */}
      <div className="px-6 pt-6 pb-2">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <i className="bi bi-people-fill text-blue-600"></i>
              {t('audienceSettings.titles.main')}
            </h1>
            <p className="text-xs text-gray-600 mt-1">{t('audienceSettings.titles.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <i className="bi bi-shield-check text-blue-600 text-sm"></i>
                <span className="text-xs font-medium text-blue-700">{t('audienceSettings.labels.smartTargeting')}</span>
              </div>
            </div>
          </div>
        </div>

       

       
      </div>

      <div className="famous-clicker-audience-account-view-tab-content famous-clicker-audience-active">
        <div className="famous-clicker-audience-audience-settings-container">
          {/* Left Column: Set My Audience */}
          <div className="famous-clicker-audience-left-div">
            <SetMyAudience
              chips={chips}
              selectedChip={selectedChip}
              handleSelectChip={handleSelectChip}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              addFilterItem={addFilterItem}
              audienceItems={audienceItems}
              selectedAudience={selectedAudience}
              handleAudienceChange={handleAudienceChange}
              filterItem={filterItem}
              removeFilterItem={removeFilterItem}
              setFilterItems={setFilterItems}
              openModal={openModal}
              notify={notify}
              isSearching={isSearching}
              handleSearch={handleSearch}
              handleAddKeyword={handleAddKeyword}
              saveRuleButtonRef={saveRuleButtonRef}
            />
          </div>

          {/* Right Column: My Rules */}
          <div className="famous-clicker-audience-right-div">
            <AudienceMyRules
              savedAudienceRules={savedAudienceRules}
              openDropdown={openDropdown}
              toggleDropdown={toggleDropdown}
              removeSavedAudienceRule={removeSavedAudienceRule}
              changeStatusSavedAudienceRule={changeStatusSavedAudienceRule}
              modifySavedAudienceRule={modifySavedAudienceRule}
              dropdownRef={dropdownRef}
              expandedRules={expandedRules}
              toggleExpandRule={toggleExpandRule}
            />
          </div>

          {/* Save Rule Modal */}
          {isModalOpen && (
            <Modal
              isOpen={isModalOpen}
              onClose={closeModal}
              onSave={saveRule}
              selectedRuleName={ruleName}
            />
          )}
        </div>
      </div>

      {/* Tutorial Complete Modal */}
      <TutorialCompleteModal />
    </div>
  );
};

export default AudienceSettings;